// src/pages/admin/EmbryoImages.tsx
import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Image as ImageIcon } from "lucide-react";
import Header from "../../components/layouts/header";
import ApiService from "../../services/api";
import JSZip from "jszip";
import { ConfirmModal } from "../../components/modal/ConfirmModal";
import { SuccessModal } from "../../components/modal/SuccessModal";

const EmbryoImages: React.FC = () => {
  const { sample_uuid } = useParams<{ sample_uuid: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const embryo_id = location.state?.embryo_id || "";

  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    fetchImages();
  }, [sample_uuid]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const data = await ApiService.fetchEmbryoImages(sample_uuid!);

      const sortedImages = data?.images
        ? [...data.images].sort((a: string, b: string) => {
            const nameA = getFileName(a).toLowerCase();
            const nameB = getFileName(b).toLowerCase();
            return nameA.localeCompare(nameB, undefined, {
              numeric: true,
              sensitivity: "base",
            });
          })
        : [];

      setImages(sortedImages);
    } catch (err) {
      console.error("Failed to fetch images:", err);
    } finally {
      setLoading(false);
    }
  };

  const getFileName = (url: string): string => {
    try {
      const pathname = new URL(url).pathname;
      return pathname.split("/").pop() || "unknown";
    } catch {
      return "unknown";
    }
  };

  const downloadAllImages = async () => {
    if (images.length === 0) return;

    // Show confirmation for large batches
    if (images.length > 20) {
      setShowConfirmModal(true);
      return;
    }

    await startDownload();
  };

  const startDownload = async () => {
    setShowConfirmModal(false);
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      let completed = 0;

      // Download images one by one with delay
      for (let i = 0; i < images.length; i++) {
        const url = images[i];
        const fileName = getFileName(url);

        try {
          // Create a temporary link and trigger download
          const link = document.createElement("a");
          link.href = url;
          link.download = fileName;
          link.style.display = "none";

          // Some browsers require the link to be in the DOM
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          completed++;
          setDownloadProgress(Math.round((completed / images.length) * 100));

          // Add delay between downloads to prevent browser blocking
          // Adjust delay based on total images
          const delay =
            images.length > 50 ? 800 : images.length > 20 ? 600 : 400;
          if (i < images.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        } catch (error) {
          console.error(
            `Failed to download image ${i + 1} (${fileName}):`,
            error
          );
          completed++;
          setDownloadProgress(Math.round((completed / images.length) * 100));
        }
      }

      // Show success message
      setTimeout(() => {
        setShowSuccessModal(true);
      }, 500);
    } catch (error) {
      console.error("Failed to download images:", error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };
  // const downloadAllImages = async () => {
  //   if (images.length === 0) return;

  //   // Show confirmation for large batches
  //   if (images.length > 20) {
  //     const confirmed = window.confirm(
  //       `This will download ${images.length} images individually. Your browser may ask for permission to download multiple files. Continue?`
  //     );
  //     if (!confirmed) return;
  //   }

  //   setIsDownloading(true);
  //   setDownloadProgress(0);

  //   try {
  //     let completed = 0;

  //     // Download images one by one with delay
  //     for (let i = 0; i < images.length; i++) {
  //       const url = images[i];
  //       const fileName = getFileName(url);

  //       try {
  //         // Create a temporary link and trigger download
  //         const link = document.createElement("a");
  //         link.href = url;
  //         link.download = fileName;
  //         link.style.display = "none";

  //         // Some browsers require the link to be in the DOM
  //         document.body.appendChild(link);
  //         link.click();
  //         document.body.removeChild(link);

  //         completed++;
  //         setDownloadProgress(Math.round((completed / images.length) * 100));

  //         // Add delay between downloads to prevent browser blocking
  //         // Adjust delay based on total images
  //         const delay =
  //           images.length > 50 ? 800 : images.length > 20 ? 600 : 400;
  //         if (i < images.length - 1) {
  //           await new Promise((resolve) => setTimeout(resolve, delay));
  //         }
  //       } catch (error) {
  //         console.error(
  //           `Failed to download image ${i + 1} (${fileName}):`,
  //           error
  //         );
  //         completed++;
  //         setDownloadProgress(Math.round((completed / images.length) * 100));
  //       }
  //     }

  //     // Show success message
  //     setTimeout(() => {
  //       alert(
  //         `Started downloading ${images.length} images. Please check your downloads folder. Note: Your browser may have organized them or asked for permission.`
  //       );
  //     }, 500);
  //   } catch (error) {
  //     console.error("Failed to download images:", error);
  //     alert("An error occurred while downloading. Please try again.");
  //   } finally {
  //     setIsDownloading(false);
  //     setDownloadProgress(0);
  //   }
  // };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Modals */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={startDownload}
        imageCount={images.length}
        message={`This will download ${images.length} images individually. Your browser may ask for permission to download multiple files.`}
        title="Download All Images"
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        imageCount={images.length}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button & Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                  Embryo Images
                </h2>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <span className="font-medium">Embryo ID:</span>{" "}
                    <span className="font-semibold text-gray-900">
                      {embryo_id}
                    </span>
                  </p>
                  <p>
                    <span className="font-medium">Total Images:</span>{" "}
                    <span className="font-semibold text-gray-900">
                      {images.length}
                    </span>
                  </p>
                </div>
              </div>

              {images.length > 0 && (
                <button
                  onClick={downloadAllImages}
                  disabled={isDownloading}
                  className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {isDownloading
                    ? `Processing... ${downloadProgress}%`
                    : `Download All (${images.length})`}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Images Gallery */}
        <div className="mb-6">
          {loading ? (
            <div className="text-center py-24">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading images...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No Information Recorded.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
              {images.map((url, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="aspect-square relative bg-gray-100">
                    <img
                      src={url}
                      alt={`Embryo ${index + 1}`}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3">
                    <p
                      className="text-xs text-gray-600 truncate"
                      title={getFileName(url)}
                    >
                      {getFileName(url)}
                    </p>
                    <a
                      href={url}
                      download={getFileName(url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image Counter for Mobile */}
        {images.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-900">
                {images.length}
              </span>{" "}
              images
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbryoImages;
